import * as React from "react";
import { BaseModal } from "../../../atoms/base-modal";
import { ApplyStatus } from "../../../../application/domain/application/application-entity";
import { useState } from "react";
import { matchType } from "../../../../application/domain/base/base-type";
import {
  ApplicationStatusTag,
  StatusRadios,
} from "../../../molecules/status-tag";
import { toErrorMessage } from "../../../../application/service/smart-apply/applications/error-messages";
import { SmartApplyR } from "../../../pages/smart-apply/route";
import { BatchUpdateStatusResult } from "../../../../application/service/smart-apply/applications/applications-service";
import { convertToFormattedId } from "@graffer-inc/cloud-gov-core";
import { Checkbox, StatusTag } from "@graffer-inc/console-design-kit";

interface StatusUpdateModalProps {
  isActive: boolean;
  onUpdateStatus: (appl: ApplyStatus, skipNotification: boolean) => void;
  onClose: () => void;
  result?: BatchUpdateStatusResult;
}

type OperationState = "SELECT" | "CONFIRM" | "PROCESS";

const checkProcessingStatus = (result?: BatchUpdateStatusResult) => {
  if (!result) {
    return "PROCESSING";
  }
  if (result.failure) {
    return "FAILURE";
  }
  if (result.done) {
    return "DONE";
  }
  throw new Error(`invalid result type ${result}`);
};

export const BatchStatusUpdateModal: React.FunctionComponent<
  StatusUpdateModalProps
> = ({ isActive, onUpdateStatus, onClose, result }) => {
  const [nextStatus, setNextStatus] = useState<ApplyStatus | "">("");
  const [skipNotification, setSkipNotification] = useState(false);
  const [pageStatus, setPageState] = useState<OperationState>("SELECT");
  const statusText = matchType(nextStatus, {
    "": undefined,
    UNTREATED: undefined,
    WIP: undefined,
    CLOSED_COMPLETED: "完了",
    CLOSED_REJECTED: "差し戻し",
    CLOSED_WITHDRAWN: "取下げ",
  });
  const shouldNotif = nextStatus.startsWith("CLOSED_");
  return (
    <BaseModal
      isActive={isActive}
      title={matchType(pageStatus, {
        SELECT: "対応ステータス一括更新",
        CONFIRM: "一括更新内容の確認",
        PROCESS: "対応ステータス一括更新",
      })}
      onClose={onClose}
    >
      {matchType(pageStatus, {
        SELECT: (
          <>
            <section className="modal-card-body">
              <label htmlFor="name" className="label">
                変更先
              </label>
              <div className="field is-horizontal">
                <div className="field-body">
                  <div className="field">
                    <div className="control">
                      <StatusRadios
                        filtered={[
                          "CLOSED_COMPLETED",
                          "CLOSED_REJECTED",
                          "CLOSED_WITHDRAWN",
                        ]}
                        current={nextStatus}
                        onChange={setNextStatus}
                      />
                    </div>
                  </div>
                  <div className="field">
                    <div className="control">
                      <StatusRadios
                        filtered={["UNTREATED", "WIP"]}
                        current={nextStatus}
                        onChange={setNextStatus}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {shouldNotif && (
                <Checkbox
                  label={"通知設定"}
                  description={
                    "申請者への通知をスキップする場合はチェックを入れてください。"
                  }
                  options={[
                    {
                      name: "申請者への通知をスキップする",
                      value: "skipNotification",
                    },
                  ]}
                  values={skipNotification ? ["skipNotification"] : []}
                  onChange={() => setSkipNotification((oldValue) => !oldValue)}
                />
              )}
            </section>
            <footer className="modal-card-foot">
              <button
                className="button is-failure is-fullwidth"
                onClick={onClose}
              >
                キャンセル
              </button>
              <button
                className={"button is-info is-fullwidth"}
                onClick={() => {
                  setPageState("CONFIRM");
                }}
                data-for-rpa-id={"confirm-update"}
                disabled={!nextStatus}
              >
                更新内容の確認へ進む
              </button>
            </footer>
          </>
        ),
        CONFIRM: (
          <>
            <section className="modal-card-body">
              <div className="field">
                <div className="field">
                  <label htmlFor="name" className="label">
                    変更先
                  </label>
                  <div className="control">
                    <ApplicationStatusTag
                      hiddenCheckIcon
                      status={nextStatus as ApplyStatus}
                    />
                  </div>
                </div>
              </div>
              {shouldNotif && (
                <div className="notification has-text-weight-bold content">
                  <p className="mb-1">
                    ステータス更新時に以下の処理が実行されます。
                  </p>
                  <ul>
                    <li>
                      {skipNotification
                        ? `申請者への${statusText}通知メールをスキップする`
                        : `申請者への${statusText}通知メールの送信`}
                    </li>
                  </ul>
                </div>
              )}
            </section>
            <footer className="modal-card-foot">
              <button
                className="button is-failure is-fullwidth"
                onClick={() => {
                  setPageState("SELECT");
                }}
              >
                選択に戻る
              </button>
              <button
                className={"button is-info is-fullwidth"}
                onClick={() => {
                  if (nextStatus !== "") {
                    onUpdateStatus(nextStatus, skipNotification);
                    setPageState("PROCESS");
                  }
                }}
                data-for-rpa-id={"status-update"}
              >
                この内容で更新する
              </button>
            </footer>
          </>
        ),
        PROCESS: (
          <>
            <section className="modal-card-body">
              {matchType(checkProcessingStatus(result), {
                PROCESSING: (
                  <>
                    <label className="has-text-weight-bold">
                      ステータスを更新しています。
                      <progress
                        className="progress is-small is-primary mt-4"
                        max="100"
                      />
                    </label>
                  </>
                ),
                DONE: !!result?.done && (
                  <>
                    {result.done.errors.length === 0 ? (
                      <div className="columns is-vcentered">
                        <div className="column is-narrow">
                          <StatusTag type={"SUCCESS"}>更新完了</StatusTag>
                        </div>
                        <div className="column">
                          {`${result.done.count} 件を更新しました。`}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="columns is-vcentered">
                          <div className="column is-narrow">
                            <StatusTag type={"WARN"}>一部失敗</StatusTag>
                          </div>
                          <div className="column">
                            {`${result.done.count} 件中 ${result.done.errors.length} 件の更新に失敗しました。`}
                          </div>
                        </div>
                        <table className="table is-fullwidth">
                          <thead>
                            <tr>
                              <th style={{ width: 230 }}>申請番号</th>
                              <th>エラー内容</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.done.errors.map((err) => (
                              <tr key={err.id}>
                                <td>
                                  <a
                                    target="_blank"
                                    href={SmartApplyR.ApplicationDetail.link({
                                      id: err.id,
                                    })}
                                    rel="noopener noreferrer"
                                  >
                                    {convertToFormattedId(err.id)}
                                  </a>
                                </td>
                                <td>{toErrorMessage(err.errorCode)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                  </>
                ),
                FAILURE: !!result?.failure && (
                  <>
                    <div className="columns is-vcenterd">
                      <div className="column is-narrow">
                        <StatusTag type={"ERROR"}>更新失敗</StatusTag>
                      </div>
                      <div className="column">
                        <p className="pt-2">{result.failure.message}</p>
                      </div>
                    </div>
                    {!!result.failure.applicationIDs &&
                      result?.failure?.applicationIDs.length > 0 && (
                        <div className="content is-flex is-justify-content-center">
                          <ul>
                            {result.failure.applicationIDs.map((id) => (
                              <li key={id}>{convertToFormattedId(id)}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                  </>
                ),
              })}
            </section>
            <footer className="modal-card-foot">
              <button
                className={"button is-fullwidth"}
                disabled={!result}
                onClick={onClose}
              >
                閉じる
              </button>
            </footer>
          </>
        ),
      })}
    </BaseModal>
  );
};
